<template>
  <div>
    <v-card-title>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter"
      />
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :rows-per-page-items="[25,50,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          @click="toggleSelected(props.item)"
        >
          <td>
            {{ props.item.description }}
          </td>
          <td>
            {{ getAddressEnrichmentPercent(props.item.holders) }}
          </td>
          <td>
            {{ getNetAmountPercent(props.item.holders, props.item.type) }}
          </td>
          <td>
            {{ getIbanPercent(props.item.holders) }}
          </td>
          <td>
            {{ getDDaPercent(props.item.holders) }}
          </td>
          <td>
            {{ getGrossAmount(props.item.holders)|toCurrency }}
          </td>
          <td>
            {{ getNettoAmount(props.item.holders)|toCurrency }}
          </td>
          <td>
            {{ parseFloat(props.item.amount) || 0|toCurrency }}
          </td>
          <td>
            {{ props.item.security_id }}
          </td>
          <td>
            {{ types()[props.item.type] }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.expected_payout_date"
            />
          </td>
          <td>
            <DateDisplay
              :date="props.item.created_at"
            />
          </td>
          <td class="text-xs">
            <v-tooltip
              v-if="props.item.import_tax_calculation"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => showTaxCalculationDialog(props.item)"
                >
                  <v-icon>publish</v-icon>
                </v-btn>
              </template>
              <span>Upload Tax Calculation CSV2</span>
            </v-tooltip>
            <v-tooltip
              v-if="(props.item.security || isAutomaticCashbackPayout(props.item)) && props.item.holders"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => showDialog(props.item)"
                >
                  <v-icon>check</v-icon>
                </v-btn>
              </template>
              <span>Approve</span>
            </v-tooltip>
            <v-chip
              v-if="!(props.item.security || isAutomaticCashbackPayout(props.item))"
              class="ma-2"
              color="red"
              text-color="white"
            >
              Missing Security
            </v-chip>
            <v-btn
              dark
              flat
              icon
              color="primary"
              @click.stop="() => downloadCsv(props.item.payout_id)"
            >
              <v-icon>download</v-icon>
            </v-btn>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showDeleteDialog(props.item)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Payout</span>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="props.item.payout_id && selected === props.item.payout_id">
          <td colspan="11">
            <PayoutDetails
              :payout-id="props.item.payout_id"
              :payout="props.item"
              :holders="props.item.holders"
              :show-holders="true"
              :show-holders-address="true"
              :show-holders-payback-information="true"
              :show-address-retry-action="true"
              :show-bank-information-retry-action="true"
              :show-pdf-download-action="false"
              :show-set-period-retry-action="true"
              :show-calc-grossamount-retry-action="true"
              :show-tax-calculation-retry-action="true"
              :show-tax-trait-enrichment-retry-action="true"
              :allow-to-delete-holders="true"
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <ApprovalsApproveDialog
      key="approve_dialog"
      :visible="isDialogActive()"
      :payout="currentPayout"
      @close="hideDialog()"
      @submit="onApprove()"
    />

    <DeleteDialog
      key="delete_dialog"
      :visible="isDeleteDialogActive()"
      :payout="currentPayout"
      @close="hideDeleteDialog()"
      @submit="onDelete()"
    />

    <TaxCalculationUploadCSVDialog
      key="tax_calculation_dialog"
      :visible="isTaxCalculationDialogActive()"
      :payout="currentPayout"
      @close="hideTaxCalculationDialog()"
      @fileUpload="(file) => onTaxCalculationUploadCsv(file)"
    />

    <v-dialog
      v-model="csvUploadingDialog"
      persistent
      width="300"
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text>
          Please wait while uploading CSV.
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="csvResultDialog"
      width="800"
    >
      <v-card
        color="primary"
        dark
        class="pa-3"
      >
        <p class="font-weight-black">
          Uploading CSV finished - Result:
        </p>
        <p>Total in CSV: {{ csvUploadingResult.totalInCsv }}</p>
        <p>Total Valid: {{ csvUploadingResult.totalImported }}</p>
        <p>Total Saved to DB: {{ csvUploadingResult.totalDBInserts }}</p>
        <p>Total Failed: {{ csvUploadingResult.totalFailed }}</p>
        <p
          v-for="item in csvUploadingResult.failedReasons"
          :key="item.index"
          style="background-color: red;"
          class="pa-1"
        >
          {{ item.message }}
        </p>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :auto-height="true"
    >
      <ul v-if="snackbarErrors">
        <li
          v-for="snackbarError in snackbarErrors"
          :key="snackbarError.message"
        >
          {{ snackbarError.message }}
        </li>
      </ul>

      <div
        v-if="snackbarMessage"
      >
        {{ snackbarMessage }}
      </div>

      <v-btn
        color="primary"
        text
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
    <HolderErrorMessagesDialog
      :errors="holderApprovalErrors"
      :visible="isHolderErrorMessagesDialogActive"
      approval-step="bankTransferApproval"
      @close="isHolderErrorMessagesDialogActive = false"
    />
  </div>
</template>

<script>
import BigNumber from 'bignumber.js';
import ApprovalsApproveDialog from './dialogs/payout/ApprovalsApproveDialog';
import TaxCalculationUploadCSVDialog from './dialogs/payout/TaxCalculationUploadCSVDialog';
import PayoutDetails from '@/components/payoutsAndTaxes/payout/shared/PayoutDetails';
import { downloadCsvFile } from '@/helpers';
import DeleteDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/DeleteDialog';
import { downloadFromS3 } from '@/helpers/axiosS3Downloader';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';
import HolderErrorMessagesDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/HolderErrorMessagesDialog';

export default {
  name: 'BankTransferApprovals',
  components: {
    HolderErrorMessagesDialog,
    PayoutDetails,
    ApprovalsApproveDialog,
    DeleteDialog,
    TaxCalculationUploadCSVDialog,
    DateDisplay,
  },
  data() {
    return {
      snackbarShow: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      snackbarErrors: [],
      holderApprovalErrors: [],
      isHolderErrorMessagesDialogActive: false,
      selected: 0,
      currentPayout: {},
      showContractId: null,
      isLoading: false,
      isShowing: false,
      approvalDialog: false,
      deleteDialogs: false,
      uploadTaxCalculationCSVDialogs: false,
      pagination: {
        rowsPerPage: 25,
      },
      csvUploadingDialog: false,
      csvUploadingResult: {
        totalDBInserts: 0,
        totalFailed: 0,
        totalImported: 0,
        totalInCsv: 0,
        failedReasons: [],
      },
      csvResultDialog: false,
      search: '',
      headers: [
        { text: 'Payout', value: 'description' },
        { text: 'Address (%)', value: 'address_enrichment_complete_percent' },
        { text: 'NetAmount (%)', value: 'net_amount_complete_percent' },
        { text: 'IBAN (%)', value: 'iban_complete_percent' },
        { text: 'DDA (%)', value: 'dda_complete_percent' },
        { text: 'Amount (Gross)', value: 'gross_amount' },
        { text: 'Amount (After taxes)', value: 'netto_amount' },
        { text: 'Amount', value: 'amount' },
        { text: 'Security ID', value: 'security_id' },
        { text: 'Type', value: 'type' },
        { text: 'Expected Payout Date', value: 'expected_payout_date' },
        { text: 'Created At', value: 'created_at' },
        { text: 'Actions', value: 'actions' },
      ],
    };
  },
  computed: {
    payoutHolderPendingApprovals() {
      return this.$store.getters.getPayoutsBankTransferApprovalPending;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.payoutHolderPendingApprovals :
        this.payoutHolderPendingApprovals.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    types() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.payout || !data.payout.types) {
        return {};
      }

      return data.payout.types;
    },
    getGrossAmount(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }

      const initialSumValue = 0;

      return holders.reduce(
        (sum, holder) => sum + holder.payback_information.gross_amount.total || 0,
        initialSumValue,
      );
    },
    getNettoAmount(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }

      const initialSumValue = 0;

      return holders.reduce(
        (sum, holder) => sum + holder.payback_information.net_amount.total || 0,
        initialSumValue,
      );
    },
    getAddressEnrichmentPercent(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }

      const initialValue = 0;

      const countComplete = holders.reduce(
        (sum, holder) => sum +
          ((holder.address && holder.address.postal_code && holder.address.city &&
            holder.address.street && (holder.address.last_name || holder.address.company_name))
            ? ((holder.address.last_name && holder.address.last_name.length > 0)
            || (holder.address.company_name && holder.address.company_name.length > 0))
            : 0),
        initialValue,
      );

      const percentage = new BigNumber(100).dividedBy(holders.length).multipliedBy(countComplete).toFixed(2);

      return `${percentage}%`;
    },
    getNetAmountPercent(holders, payoutType) {
      if (!holders) {
        return 'Expand to calculate';
      }

      const initialValue = 0;

      const countComplete = holders.reduce(
        (sum, holder) => sum +
          ((holder.payback_information
            && holder.payback_information.net_amount
            // eslint-disable-next-line max-len
            && ((payoutType === 'notional' && holder.payback_information.net_amount.total) || (payoutType !== 'notional' && holder.payback_information.net_amount.total > 0)))
            ? 1
            : 0),
        initialValue,
      );

      const percentage = new BigNumber(100).dividedBy(holders.length).multipliedBy(countComplete).toFixed(2);

      return `${percentage}%`;
    },
    getIbanPercent(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }

      const initialValue = 0;

      const countComplete = holders.reduce((sum, holder) => {
        const accounts = holder.payment_information && holder.payment_information.accounts;
        const hasAccount = accounts && accounts.some(
          account => account.type === 'banktransfer' || account.type === 'mangopay',
        );

        return sum + (hasAccount ? 1 : 0);
      }, initialValue);

      const percentage = new BigNumber(100)
        .dividedBy(holders.length)
        .multipliedBy(countComplete)
        .toFixed(2);

      return `${percentage}%`;
    },
    getDDaPercent(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }

      const initialValue = 0;

      const countComplete = holders.reduce((sum, holder) => {
        const accounts = holder.payment_information && holder.payment_information.accounts;
        const hasAccount = accounts && accounts.some(account => account.type === 'ddaccount');

        return sum + (hasAccount ? 1 : 0);
      }, initialValue);

      const percentage = new BigNumber(100)
        .dividedBy(holders.length)
        .multipliedBy(countComplete)
        .toFixed(2);

      return `${percentage}%`;
    },
    onApprove() {
      this.$store.dispatch('callPayoutBankTransferApprovalApprove', this.currentPayout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout successfully approved';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.holderApprovalErrors = error.response.data || [];
          this.isHolderErrorMessagesDialogActive = true;
        });
    },
    onDelete() {
      this.$store.dispatch('callPayoutDelete', this.currentPayout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout Deleted!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    isDeleteDialogActive() {
      return this.deleteDialog === true;
    },
    showDeleteDialog(item) {
      this.currentPayout = item;
      this.deleteDialog = true;
      this.$forceUpdate();
    },
    hideDeleteDialog() {
      this.deleteDialog = false;
      this.$forceUpdate();
    },
    isDialogActive() {
      return this.approvalDialog === true;
    },
    showDialog(item) {
      this.currentPayout = item;
      this.approvalDialog = true;
      this.$forceUpdate();
    },
    hideDialog() {
      this.approvalDialog = false;
      this.$forceUpdate();
    },
    isAutomaticCashbackPayout(payout) {
      return payout.type === 'cashback_v2';
    },
    toggleSelected(payout) {
      this.selected = this.selected === payout.payout_id ? null : payout.payout_id;
      this.loadPayoutHolders(payout);
    },
    loadPayoutHolders(payout) {
      if (payout.holders && payout.holders.length !== 0) {
        return;
      }

      this.$store.dispatch('fetchPayoutDataGetHolders', {
        origin: 'bankTransferApproval',
        payoutId: payout.payout_id,
      })
        .then(() => {
          this.$forceUpdate();
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    downloadCsv(payoutId) {
      this.$store
        .dispatch('fetchPayoutBankTransferDownloadCsv', payoutId)
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        });
    },
    isTaxCalculationDialogActive() {
      return this.uploadTaxCalculationCSVDialog === true;
    },
    showTaxCalculationDialog(item) {
      this.currentPayout = item;
      this.uploadTaxCalculationCSVDialog = true;
      this.$forceUpdate();
    },
    hideTaxCalculationDialog() {
      this.uploadTaxCalculationCSVDialog = false;
      this.$forceUpdate();
    },
    onTaxCalculationUploadCsv(file) {
      const payoutId = this.currentPayout.payout_id;
      this.csvUploadingDialog = true;
      this.$store
        .dispatch('callPayoutTaxCalculationImportCSV', { file, payoutId })
        .then((e) => {
          this.csvUploadingDialog = false;
          this.csvUploadingResult = e;
          this.csvResultDialog = true;
          this.$store.dispatch('fetchPayoutAll');
        })
        .catch((error) => {
          this.csvUploadingDialog = false;
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
  },
};
</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/BankTransferApprovals.vue